// Deliverable Type Definitions, Status, and Configurations

// Dependency type definitions
export const DEPENDENCY_TYPES = {
  START: 'start',   // Blocks production — dependency must leave 'not_ready' before this can start
  REVIEW: 'review'  // Blocks client review — dependency must be 'approved' or 'complete'
};

// Type definitions
export const DELIVERABLE_TYPES = {
  STANDARD: 'standard',
  SOCIAL_FIRST: 'social_first',
  SCRIPTED: 'scripted',
  PODCAST: 'podcast',
  DEMO: 'demo',
  RE_EDIT: 're_edit',
  HYPE: 'hype',
  SELECTS: 'selects',
  OTHER: 'other'
};

// Status definitions
export const DELIVERABLE_STATUSES = {
  NOT_STARTED: 'not_started',
  READY: 'ready',
  WORKING_ON_IT: 'working_on_it',
  STUCK: 'stuck',
  DONE: 'done'
};

// Stage definitions
export const DELIVERABLE_STAGES = {
  NOT_READY: 'not_ready',
  ORGANIZATION: 'organization',
  SELECTS: 'selects',
  COLOR: 'color',
  POST_QUEUE: 'post_queue',
  REVISION_QUEUE: 'revision_queue',
  INTERNAL_REVIEW: 'internal_review',
  CLIENT_REVIEW: 'client_review',
  APPROVED: 'approved',
  COMPLETE: 'complete',
  OTHER: 'other'
};

// Ordered stage pipeline (for reports and progression tracking)
export const STAGE_PIPELINE_ORDER = [
  DELIVERABLE_STAGES.NOT_READY,
  DELIVERABLE_STAGES.ORGANIZATION,
  DELIVERABLE_STAGES.SELECTS,
  DELIVERABLE_STAGES.COLOR,
  DELIVERABLE_STAGES.POST_QUEUE,
  DELIVERABLE_STAGES.REVISION_QUEUE,
  DELIVERABLE_STAGES.INTERNAL_REVIEW,
  DELIVERABLE_STAGES.CLIENT_REVIEW,
  DELIVERABLE_STAGES.APPROVED,
  DELIVERABLE_STAGES.COMPLETE,
];

// Aspect ratio options
export const ASPECT_RATIOS = [
  { value: '16:9', label: '16:9 (Landscape)' },
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '1:1', label: '1:1 (Square)' },
  { value: '4:5', label: '4:5 (Vertical)' },
  { value: 'custom', label: 'Custom' }
];

// Weight options (Fibonacci story points)
export const DELIVERABLE_WEIGHTS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 8, label: '8' },
  { value: 13, label: '13' },
  { value: 21, label: '21' }
];

// Get label for deliverable weight
export const getDeliverableWeightLabel = (weight) => {
  if (weight == null) return 'Not set';
  const found = DELIVERABLE_WEIGHTS.find(w => w.value === weight);
  return found ? found.label : String(weight);
};

// Type configurations with icons and colors
export const DELIVERABLE_TYPE_CONFIG = {
  [DELIVERABLE_TYPES.STANDARD]: {
    label: 'Standard',
    value: 'standard',
    icon: 'film',
    color: 'hsl(204, 71%, 53%)', // info blue
    className: 'is-info'
  },
  [DELIVERABLE_TYPES.SOCIAL_FIRST]: {
    label: 'Social First',
    value: 'social_first',
    icon: 'share-nodes',
    color: 'hsl(278, 99%, 42%)', // purple
    className: 'is-purple'
  },
  [DELIVERABLE_TYPES.SCRIPTED]: {
    label: 'Scripted',
    value: 'scripted',
    icon: 'file-lines',
    color: 'hsl(217, 71%, 53%)', // primary blue
    className: 'is-primary'
  },
  [DELIVERABLE_TYPES.PODCAST]: {
    label: 'Podcast',
    value: 'podcast',
    icon: 'microphone',
    color: 'hsl(141, 53%, 53%)', // success green
    className: 'is-success'
  },
  [DELIVERABLE_TYPES.DEMO]: {
    label: 'Demo',
    value: 'demo',
    icon: 'person-chalkboard',
    color: 'hsl(48, 100%, 67%)', // warning yellow
    className: 'is-warning'
  },
  [DELIVERABLE_TYPES.RE_EDIT]: {
    label: 'Re-edit',
    value: 're_edit',
    icon: 'edit',
    color: 'hsl(271, 100%, 71%)', // light purple
    className: 'is-link'
  },
  [DELIVERABLE_TYPES.HYPE]: {
    label: 'Hype',
    value: 'hype',
    icon: 'fire',
    color: 'hsl(14, 100%, 53%)', // orange
    className: 'is-danger'
  },
  [DELIVERABLE_TYPES.SELECTS]: {
    label: 'Video Clips',
    value: 'selects',
    icon: 'film-simple',
    color: 'hsl(171, 100%, 41%)', // turquoise
    className: 'is-primary'
  },
  [DELIVERABLE_TYPES.OTHER]: {
    label: 'Other',
    value: 'other',
    icon: 'folder',
    color: 'hsl(0, 0%, 48%)', // gray
    className: 'is-grey'
  }
};

// Stage configurations with icons and colors
export const DELIVERABLE_STAGE_CONFIG = {
  [DELIVERABLE_STAGES.NOT_READY]: {
    label: 'Not Ready',
    value: 'not_ready',
    icon: 'ban',
    color: 'hsl(0, 0%, 29%)', // darker gray for better visibility
    className: 'is-dark'
  },
  [DELIVERABLE_STAGES.ORGANIZATION]: {
    label: 'Organization',
    value: 'organization',
    icon: 'folder-tree',
    color: 'hsl(217, 71%, 53%)', // primary blue
    className: 'is-primary'
  },
  [DELIVERABLE_STAGES.SELECTS]: {
    label: 'Video Clips',
    value: 'selects',
    icon: 'film-simple',
    color: 'hsl(171, 100%, 41%)', // turquoise
    className: 'is-primary'
  },
  [DELIVERABLE_STAGES.COLOR]: {
    label: 'Color',
    value: 'color',
    icon: 'palette',
    color: 'hsl(278, 99%, 42%)', // purple
    className: 'is-purple'
  },
  [DELIVERABLE_STAGES.POST_QUEUE]: {
    label: 'Post Queue',
    value: 'post_queue',
    icon: 'list-check',
    color: 'hsl(34, 100%, 56%)', // warning yellow
    className: 'is-warning'
  },
  [DELIVERABLE_STAGES.REVISION_QUEUE]: {
    label: 'Revision Queue',
    value: 'revision_queue',
    icon: 'pen-to-square',
    color: 'hsl(14, 100%, 53%)', // orange
    className: 'is-danger'
  },
  [DELIVERABLE_STAGES.INTERNAL_REVIEW]: {
    label: 'Internal Review',
    value: 'internal_review',
    icon: 'user-check',
    color: 'hsl(204, 71%, 53%)', // info blue
    className: 'is-info'
  },
  [DELIVERABLE_STAGES.CLIENT_REVIEW]: {
    label: 'Client Review',
    value: 'client_review',
    icon: 'users',
    color: 'hsl(271, 100%, 71%)', // light purple
    className: 'is-link'
  },
  [DELIVERABLE_STAGES.APPROVED]: {
    label: 'Approved',
    value: 'approved',
    icon: 'circle-check',
    color: 'hsl(141, 53%, 53%)', // success green
    className: 'is-success'
  },
  [DELIVERABLE_STAGES.COMPLETE]: {
    label: 'Complete',
    value: 'complete',
    icon: 'flag-checkered',
    color: 'hsl(158, 80%, 35%)', // dark green
    className: 'is-success'
  },
  [DELIVERABLE_STAGES.OTHER]: {
    label: 'Other',
    value: 'other',
    icon: 'ellipsis',
    color: 'hsl(0, 0%, 48%)', // gray
    className: 'is-grey'
  }
};

// Status configurations with icons and colors
export const DELIVERABLE_STATUS_CONFIG = {
  [DELIVERABLE_STATUSES.NOT_STARTED]: {
    label: 'Not Started',
    value: 'not_started',
    icon: 'circle',
    color: 'hsl(0, 0%, 71%)', // grey
    className: 'is-dark'
  },
  [DELIVERABLE_STATUSES.READY]: {
    label: 'Ready',
    value: 'ready',
    icon: 'check-circle',
    color: 'hsl(228.5, 88.4%, 62.9%)', // info
    className: 'is-info'
  },
  [DELIVERABLE_STATUSES.WORKING_ON_IT]: {
    label: 'Working On It',
    value: 'working_on_it',
    icon: 'spinner',
    color: 'hsl(34, 100%, 56%)', // warning
    className: 'is-warning'
  },
  [DELIVERABLE_STATUSES.STUCK]: {
    label: 'Stuck',
    value: 'stuck',
    icon: 'exclamation-triangle',
    color: 'hsl(348, 86%, 61%)', // danger
    className: 'is-danger'
  },
  [DELIVERABLE_STATUSES.DONE]: {
    label: 'Done',
    value: 'done',
    icon: 'check-double',
    color: 'hsl(158, 80%, 35%)', // success
    className: 'is-success'
  }
};

// Get deliverable type options for dropdowns
export const getDeliverableTypeOptions = () => {
  return Object.values(DELIVERABLE_TYPE_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    icon: config.icon, // SearchableSelect expects string format
    color: config.color
  }));
};

// Get deliverable status options for dropdowns
export const getDeliverableStatusOptions = () => {
  return Object.values(DELIVERABLE_STATUS_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    icon: config.icon, // SearchableSelect expects string format
    color: config.color
  }));
};

// Get deliverable stage options for dropdowns
export const getDeliverableStageOptions = () => {
  return Object.values(DELIVERABLE_STAGE_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    icon: config.icon, // SearchableSelect expects string format
    color: config.color
  }));
};

// Get icon for deliverable type
export const getDeliverableTypeIcon = (type) => {
  const config = DELIVERABLE_TYPE_CONFIG[type];
  return config ? config.icon : 'folder';
};

// Get color for deliverable type
export const getDeliverableTypeColor = (type) => {
  const config = DELIVERABLE_TYPE_CONFIG[type];
  return config ? config.color : 'hsl(0, 0%, 48%)';
};

// Get class name for deliverable type
export const getDeliverableTypeClassName = (type) => {
  const config = DELIVERABLE_TYPE_CONFIG[type];
  return config ? config.className : 'is-grey';
};

// Get label for deliverable type
export const getDeliverableTypeLabel = (type) => {
  const config = DELIVERABLE_TYPE_CONFIG[type];
  return config ? config.label : 'Unknown';
};

// Get icon for deliverable status
export const getDeliverableStatusIcon = (status) => {
  const config = DELIVERABLE_STATUS_CONFIG[status];
  return config ? config.icon : 'question-circle';
};

// Get color for deliverable status
export const getDeliverableStatusColor = (status) => {
  const config = DELIVERABLE_STATUS_CONFIG[status];
  return config ? config.color : 'hsl(0, 0%, 48%)';
};

// Get class name for deliverable status
export const getDeliverableStatusClassName = (status) => {
  const config = DELIVERABLE_STATUS_CONFIG[status];
  return config ? config.className : 'is-grey';
};

// Get label for deliverable status
export const getDeliverableStatusLabel = (status) => {
  const config = DELIVERABLE_STATUS_CONFIG[status];
  return config ? config.label : 'Unknown';
};

// Get icon for deliverable stage
export const getDeliverableStageIcon = (stage) => {
  const config = DELIVERABLE_STAGE_CONFIG[stage];
  return config ? config.icon : 'question-circle';
};

// Get class name for deliverable stage
export const getDeliverableStageClassName = (stage) => {
  const config = DELIVERABLE_STAGE_CONFIG[stage];
  return config ? config.className : 'is-grey';
};

// Get label for deliverable stage
export const getDeliverableStageLabel = (stage) => {
  const config = DELIVERABLE_STAGE_CONFIG[stage];
  return config ? config.label : 'Unknown';
};

// Check if deliverable is overdue (due date is before today, not including today)
export const isDeliverableOverdue = (deliverable) => {
  if (!deliverable.dueDate || deliverable.status === DELIVERABLE_STATUSES.DONE) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = deliverable.dueDate.toDate ? deliverable.dueDate.toDate() : new Date(deliverable.dueDate);
  const dueDateNormalized = new Date(dueDate);
  dueDateNormalized.setHours(0, 0, 0, 0);

  return dueDateNormalized < today;
};

// Check if deliverable is due today (for warning state)
export const isDeliverableDueToday = (deliverable) => {
  if (!deliverable.dueDate || deliverable.status === DELIVERABLE_STATUSES.DONE) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = deliverable.dueDate.toDate ? deliverable.dueDate.toDate() : new Date(deliverable.dueDate);
  const dueDateNormalized = new Date(dueDate);
  dueDateNormalized.setHours(0, 0, 0, 0);

  return dueDateNormalized.getTime() === today.getTime();
};